import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Button, Divider, Typography, Input } from 'antd'
import {
  SearchOutlined,
  DownOutlined,
  CloseOutlined,
  QrcodeOutlined,
  GlobalOutlined,
  ShoppingCartOutlined,
  DollarCircleFilled,
  FacebookOutlined,
  TwitterOutlined,
  LinkedinOutlined,
  InstagramOutlined,
  GithubOutlined,
  CodeOutlined,
  SolutionOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import {
  MdComputer,
  MdLocalHospital,
  MdBusiness,
  MdSettings,
  MdPerson,
  MdTrendingUp,
  MdBuild,
  MdSchool,
  MdPlayCircleOutline,
  MdPeopleOutline,
  MdAccessTime,
  MdStar,
  MdArrowForward,
  MdLanguage,
} from 'react-icons/md'
import styled, { createGlobalStyle } from 'styled-components'
import { useHistory, useLocation, Link } from 'react-router-dom'
import Spinner from '../../components/Spinner'
import { getAllCourses } from '../../reducers/courseReducer'
import PublicPageLayout from '../../components/PublicPageLayout'

const { Text, Paragraph } = Typography

/* ── GLOBAL STYLE ─── */
const GlobalPageStyle = createGlobalStyle`
  body {
    background: #fff;
    margin: 0;
    padding: 0;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #333;
  }
`

/* ══════════════════════════════════════════════
   LAYOUT COMPONENTS
══════════════════════════════════════════════ */
const PageWrapper = styled.div`
  background: #f4f7f9;
  min-height: 80vh;
  position: relative; /* Base for absolute/fixed ads */
`

const CategoryBar = styled.div`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 24px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`

const CategoryInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 0;
`

const CategoryTab = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 18px;
  cursor: pointer;
  border-bottom: 3px solid ${p => p.active ? 'hsl(210,55%,22%)' : 'transparent'};
  color: ${p => p.active ? 'hsl(210,55%,22%)' : '#555'};
  font-size: 12px;
  font-weight: 600;
  gap: 5px;
  min-width: 72px;
  white-space: nowrap;
  transition: all 0.2s;
  &:hover { color: hsl(210,55%,22%); }
`

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  position: relative;
`

/* ══════════════════════════════════════════════
   FLOATING GORILLA/GOOGLE STYLE ADS
   (Won't affect main content position)
══════════════════════════════════════════════ */
const GutterAdContainer = styled.div`
  position: fixed;
  top: 180px;
  width: 160px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 1580px) {
    display: none; /* Only show in "free space" on wide screens */
  }
`

const LeftAd = styled(GutterAdContainer)`
  left: calc(50% - 780px);
`

const RightAd = styled(GutterAdContainer)`
  right: calc(50% - 780px);
`

const MiniAdCard = styled.a`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 12px 24px rgba(0,0,0,0.1);
    border-color: #f59e0b;
  }

  .ad-label {
    background: #fdf2f2;
    color: #9ca3af;
    font-size: 9px;
    font-weight: 800;
    padding: 4px 10px;
    text-transform: uppercase;
    display: flex;
    justify-content: space-between;
    letter-spacing: 1px;
    border-bottom: 1px solid #f3f4f6;
  }

  .ad-img {
    width: 100%;
    height: 180px;
    background: #f3f4f6;
    object-fit: cover;
  }

  .ad-meta {
    padding: 12px;
    h5 { font-size: 14px; font-weight: 800; color: #111; margin: 0 0 4px; line-height: 1.2; }
    p { font-size: 11px; color: #6b7280; margin: 0; line-height: 1.4; }
    .cta { 
      font-size: 11px; font-weight: 700; color: #f59e0b; 
      display: inline-flex; align-items: center; gap: 4px; margin-top: 8px;
    }
  }
`

/* ══════════════════════════════════════════════
   REST OF COMPONENTS (UNCHANGED POSITION)
══════════════════════════════════════════════ */
const SearchRow = styled.div`
  position: relative;
  margin-bottom: 32px;
  input {
    width: 100%; height: 50px; border: 1px solid #d1d5db; border-radius: 8px;
    padding: 0 52px 0 18px; font-size: 15px; outline: none; background: white;
    &:focus { border-color: hsl(210,55%,22%); box-shadow: 0 0 0 2px rgba(27,67,111,0.1); }
  }
  .icon { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); color: #9ca3af; font-size: 20px; }
`

const SectionHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;
  h2 { font-size: 20px; font-weight: 800; color: #111827; margin: 0; }
  .view-all { color: hsl(210,55%,22%); font-size: 14px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 4px; }
`

const CourseGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 48px;
  @media (max-width: 1024px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`

const CourseCardBox = styled.div`
  background: white; border-radius: 8px; overflow: hidden; border: 1px solid #e5e7eb; cursor: pointer;
  transition: all 0.2s; &:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.10); transform: translateY(-3px); }
  .thumb { height: 148px; background: #e2e8f0; img { width: 100%; height: 100%; object-fit: cover; } }
  .body { 
    padding: 14px; 
    .title { font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 8px; height: 40px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
    .stars { color: #f59e0b; font-size: 13px; display: flex; align-items: center; gap: 2px; }
    .meta { display: flex; justify-content: space-between; font-size: 12px; color: #6b7280; margin-top: 8px; }
    .enroll-btn { width: 100%; height: 38px; background: hsl(33, 100%, 50%); border: none; border-radius: 4px; color: white; font-weight: 700; margin-top: 14px; cursor: pointer; }
  }
`

const PromoBanner = styled.div`
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9); border-radius: 12px; padding: 32px 40px;
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 48px;
  .text { h3 { font-size: 22px; font-weight: 800; color: #1b5e20; } p { font-size: 14px; color: #2e7d32; max-width: 520px; } }
  .promo-cta { height: 44px; padding: 0 28px; background: hsl(210,55%,22%); border: none; border-radius: 6px; color: white; font-weight: 700; cursor: pointer; }
  @media (max-width: 768px) { flex-direction: column; .text { margin-bottom: 24px; } }
`

const StatsBand = styled.div`
  display: flex; gap: 40px; background: white; border: 1px solid #e5e7eb; border-radius: 10px; padding: 20px 32px; margin-bottom: 36px;
  .stat-item { .num { font-size: 24px; font-weight: 900; color: hsl(210,55%,22%); } .lbl { font-size: 12px; color: #6b7280; } }
  @media (max-width: 768px) { flex-direction: column; text-align: center; }
`

/* ══════════════════════════════════════════════
   DATA & COMPONENT
══════════════════════════════════════════════ */
const CATS = [
  { id: 'All', name: 'All Courses', icon: <MdPlayCircleOutline /> },
  { id: 'IT', name: 'IT', icon: <MdComputer /> },
  { id: 'Health', name: 'Health', icon: <MdLocalHospital /> },
  { id: 'Business', name: 'Business', icon: <MdBusiness /> },
  { id: 'Management', name: 'Management', icon: <MdSettings /> },
  { id: 'Personal', name: 'Personal Dev', icon: <MdPerson /> },
  { id: 'Marketing', name: 'Marketing', icon: <MdTrendingUp /> },
  { id: 'Engineering', name: 'Engineering', icon: <MdBuild /> },
  { id: 'Teaching', name: 'Teaching', icon: <MdSchool /> },
  { id: 'Language', name: 'Language', icon: <MdLanguage /> },
]

const THUMBS = [
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&q=80',
  'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&q=80',
  'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400&q=80',
  'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&q=80',
  'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&q=80',
  'https://images.unsplash.com/photo-1543966888-7c1dc482a810?w=400&q=80',
  'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80',
  'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&q=80',
]

const PublicCourses = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const initialQuery = location.state?.query || ''
  const [filter, setFilter] = useState(initialQuery)
  const [activeCat, setActiveCat] = useState('All')

  useEffect(() => {
    if (location.state?.catId) setActiveCat(location.state.catId)
    dispatch(getAllCourses())
  }, [location.state, dispatch])

  const courses = useSelector(s => s.courses.data.filter(c => c.status !== 'archived'))
  const loading = useSelector(s => s.courses.loading)

  const filtered = courses.filter(c => {
    const matchSearch = String(c.name || '').toLowerCase().includes(String(filter || '').toLowerCase())
    const matchCat = activeCat === 'All' || c.category === activeCat
    return matchSearch && matchCat
  })

  useEffect(() => { setFilter(initialQuery) }, [initialQuery])

  if (loading) return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner size="large" />
    </div>
  )

  return (
    <PublicPageLayout activeLink="courses">
      <GlobalPageStyle />

      <LeftAd>
        <MiniAdCard href="https://www.taskrw.com/" target="_blank" rel="noopener noreferrer">
          <div className="ad-label"><span>Sponsored</span><CloseOutlined /></div>
          <img src="/taskrw_sidebar.png" alt="Ad" className="ad-img" />
          <div className="ad-meta">
            <h5>Earn as a Freelancer</h5>
            <p>Register your skills on TaskRw and get paid by local clients.</p>
            <span className="cta">Join TaskRw <MdArrowForward /></span>
          </div>
        </MiniAdCard>
      </LeftAd>

      <RightAd>
        <MiniAdCard href="https://www.taskrw.com/" target="_blank" rel="noopener noreferrer">
          <div className="ad-label"><span>Task Marketplace</span><CloseOutlined /></div>
          <div style={{ background: '#fffbeb', padding: '15px', textAlign: 'center' }}>
            <MdTrendingUp style={{ fontSize: 32, color: '#f59e0b' }} />
            <div style={{ fontSize: 13, fontWeight: 900, marginTop: 4 }}>High Demand</div>
            <div style={{ fontSize: 10, color: '#92400e' }}>100+ new tasks today</div>
          </div>
          <div className="ad-meta">
            <h5>Browse Available Tasks</h5>
            <p>From data entry to web design, find tasks that fit your schedule.</p>
            <span className="cta">Start Earning <MdArrowForward /></span>
          </div>
        </MiniAdCard>
      </RightAd>

      <CategoryBar>
        <CategoryInner>
          {CATS.map(cat => (
            <CategoryTab key={cat.id} active={activeCat === cat.id} onClick={() => setActiveCat(cat.id)}>
              <span style={{ fontSize: 20, marginBottom: 5 }}>{cat.icon}</span>
              {cat.name}
            </CategoryTab>
          ))}
        </CategoryInner>
      </CategoryBar>

      <PageWrapper>
        <ContentWrapper>
          <StatsBand>
            <div className="stat-item"><div className="num">4,000+</div><div className="lbl">Free Courses</div></div>
            <div className="stat-item"><div className="num">15M+</div><div className="lbl">Learners Worldwide</div></div>
            <div className="stat-item"><div className="num">200+</div><div className="lbl">Diploma Programs</div></div>
            <div className="stat-item"><div className="num">100%</div><div className="lbl">Free Registration</div></div>
          </StatsBand>

          <SearchRow>
            <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search for courses....." />
            <SearchOutlined className="icon" />
          </SearchRow>

          <SectionHeader>
            <h2>{activeCat === 'All' ? 'Most Popular Courses' : `${activeCat} Courses`}</h2>
            <span className="view-all">View All <MdArrowForward /></span>
          </SectionHeader>

          <CourseGrid>
            {filtered.length > 0 ? filtered.map((course, i) => (
              <CourseCardBox key={course._id || course.id} onClick={() => history.push(`/course/${course._id || course.id}`)}>
                <div className="thumb"><img src={course.image || THUMBS[i % THUMBS.length]} alt={course.name} /></div>
                <div className="body">
                  <div className="title">{course.name}</div>
                  <div className="stars">{[...Array(5)].map((_, si) => <MdStar key={si} />)}<span style={{ color: '#9ca3af', marginLeft: 4 }}>(2.4k)</span></div>
                  <div className="meta"><span><MdAccessTime /> 15-20 hrs</span><span><MdPeopleOutline /> 25k+</span></div>
                  <button className="enroll-btn" onClick={e => { e.stopPropagation(); history.push(`/course/${course._id || course.id}`); }}>Learn More</button>
                </div>
              </CourseCardBox>
            )) : (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
                <SearchOutlined style={{ fontSize: 48 }} />
                <p>No courses found. Try a different category or search term.</p>
              </div>
            )}
          </CourseGrid>

          <PromoBanner>
            <div className="text">
              <h3>Get Certified & Accelerate Your Career</h3>
              <p>Showcase your expertise with globally recognized certificates. Our alumni work at world-leading companies.</p>
              <button className="promo-cta" onClick={() => history.push('/register')}>Explore Certifications</button>
            </div>
          </PromoBanner>
        </ContentWrapper>
      </PageWrapper>
    </PublicPageLayout>
  )
}

export default PublicCourses
